import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ProducerSubscription {
  producer_id: string;
  plan_id: string;
  status: string;
}

/**
 * Hook to read & update a producer's subscription plan (admin only).
 */
export function useAdminProducerPlan(producerId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ['admin-producer-plan', producerId];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ProducerSubscription | null> => {
      if (!producerId) return null;
      const { data, error } = await supabase
        .from('producer_subscriptions')
        .select('producer_id, plan_id, status')
        .eq('producer_id', producerId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!producerId,
  });

  const plansQuery = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, name_sv, sort_order')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

  const changePlan = useMutation({
    mutationFn: async (planId: string) => {
      if (!producerId) throw new Error('No producerId');
      const { error } = await supabase
        .from('producer_subscriptions')
        .upsert(
          { producer_id: producerId, plan_id: planId, status: 'active' },
          { onConflict: 'producer_id' }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Invalidate effective features since plan changed
      queryClient.invalidateQueries({ queryKey: ['admin-effective-features', producerId] });
      queryClient.invalidateQueries({ queryKey: ['producer-effective-features', producerId] });
      toast({ title: 'Plan uppdaterad' });
    },
    onError: (err: any) => {
      toast({ title: 'Fel', description: err?.message || 'Försök igen.', variant: 'destructive' });
    },
  });

  return {
    subscription: query.data,
    plans: plansQuery.data ?? [],
    isLoading: query.isLoading || plansQuery.isLoading,
    changePlan: (planId: string) => changePlan.mutateAsync(planId),
    isChanging: changePlan.isPending,
  };
}
